import React, { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Divider, Popover, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { getRecentDeliveryNoteBatchesTemplate, rerunDeliveryNoteBatchTemplate } from 'apollo/templates/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { DeliveryNoteBatchStatus, RecentDeliveryNoteBatch, RerunDeliveryNoteBatchResult } from 'models/order';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import RecentBatchRow from './recent-batch-row.component';

// A generating batch is about to change, so poll it eagerly; settled batches only
// need the occasional re-signed download URL (the backend signs 30-minute URLs).
const GENERATING_POLL_INTERVAL_MS = 10 * 1000;
const SETTLED_POLL_INTERVAL_MS = 30 * 1000;

type RerunFeedback = { type: 'success' | 'error'; message: string };

const hasGeneratingBatch = (batches: RecentDeliveryNoteBatch[]): boolean =>
    batches.some(
        (batch) =>
            batch.status === DeliveryNoteBatchStatus.PROCESSING || batch.status === DeliveryNoteBatchStatus.MERGING,
    );

/**
 * DD-3592: a lab-wide list of the last 24 hours' delivery-note batches, so the
 * compiled PDF survives the tab that started it — re-download any batch, re-run
 * the failed orders of a FAILED/PARTIAL one. Rendered in the order lists' toolbar,
 * next to the batch-action checkbox.
 */
const RecentDeliveryNoteBatches: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const [rerunFeedback, setRerunFeedback] = useState<RerunFeedback | null>(null);
    const isOpen = Boolean(anchorElement);

    // Batch actions are lab work: dentists and external technicians never see them,
    // so they do not get the batch list either (the backend refuses them too).
    const isHiddenForUser =
        permissions.isDentist(currentUser.type) || permissions.isExternalTechnician(currentUser.role);

    const [pollIntervalMs, setPollIntervalMs] = useState(SETTLED_POLL_INTERVAL_MS);
    const { data, loading, error, refetch } = useQuery<{ recentDeliveryNoteBatches: RecentDeliveryNoteBatch[] }>(
        getRecentDeliveryNoteBatchesTemplate,
        {
            skip: !isOpen || isHiddenForUser,
            fetchPolicy: 'cache-and-network',
            pollInterval: pollIntervalMs,
        },
    );

    useEffect(() => {
        setPollIntervalMs(
            hasGeneratingBatch(data?.recentDeliveryNoteBatches ?? [])
                ? GENERATING_POLL_INTERVAL_MS
                : SETTLED_POLL_INTERVAL_MS,
        );
    }, [data]);

    const [rerunBatch, { loading: isRerunning }] = useMutation<{
        rerunDeliveryNoteBatch: RerunDeliveryNoteBatchResult;
    }>(rerunDeliveryNoteBatchTemplate);

    if (isHiddenForUser) {
        return null;
    }

    const batches = data?.recentDeliveryNoteBatches ?? [];

    const handleRerun = async (batchId: string) => {
        try {
            const result = await rerunBatch({ variables: { batchId } });
            setRerunFeedback({
                type: 'success',
                message: translate('orders:BATCH_RERUN_STARTED', {
                    count: result.data?.rerunDeliveryNoteBatch.total ?? 0,
                }),
            });
            await refetch();
        } catch (rerunError) {
            setRerunFeedback({ type: 'error', message: (rerunError as Error).message });
        }
    };

    const listContent = () => {
        if (error) {
            return (
                <Alert severity="error" data-test="recent-batches.error">
                    {translate('orders:RECENT_BATCHES_ERROR')}
                </Alert>
            );
        }
        if (loading && !data) {
            return (
                <Box display="flex" justifyContent="center" p={2} data-test="recent-batches.loading">
                    <CircularProgress size={24} />
                </Box>
            );
        }
        if (!batches.length) {
            return (
                <Typography variant="body2" color="text.secondary" p={1} data-test="recent-batches.empty">
                    {translate('orders:RECENT_BATCHES_EMPTY')}
                </Typography>
            );
        }
        return batches.map((batch, batchIndex) => (
            <Box key={batch._id}>
                {batchIndex > 0 ? <Divider /> : undefined}
                <RecentBatchRow batch={batch} onRerun={handleRerun} isRerunning={isRerunning} />
            </Box>
        ));
    };

    return (
        <>
            <Button
                variant="text"
                startIcon={<HistoryIcon />}
                onClick={(clickEvent) => setAnchorElement(clickEvent.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
                data-test="recent-batches.button"
            >
                {translate('orders:RECENT_BATCHES')}
            </Button>
            <Popover
                open={isOpen}
                anchorEl={anchorElement}
                onClose={() => setAnchorElement(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box p={2} sx={{ width: 420, maxHeight: 480, overflowY: 'auto' }} data-test="recent-batches.list">
                    <Typography variant="subtitle2" mb={1}>
                        {translate('orders:RECENT_BATCHES')}
                    </Typography>
                    {listContent()}
                </Box>
            </Popover>
            {rerunFeedback ? (
                <AlertSnackbar
                    key={rerunFeedback.message}
                    type={rerunFeedback.type}
                    message={rerunFeedback.message}
                    onClose={() => setRerunFeedback(null)}
                />
            ) : undefined}
        </>
    );
};

export default RecentDeliveryNoteBatches;
